import persistenceManager from './persistenceManager';
import STORAGE_TYPES from './storageTypes';

function isRecordExpired(record, memberId, memberConfig) {
  return (Date.now() - record.meta.createdAtUnix) >= memberConfig.expiresIn;
}

function createMemberIdAndKey(keyParam, options) {
  let memberKey, memberId;

  if (typeof keyParam === 'object') {
    memberKey = `${keyParam.key}-${keyParam.param}`;
    memberId = keyParam.key;
  } else if (typeof keyParam === 'string') {
    memberKey = keyParam;
    memberId = keyParam;
  } else if (typeof keyParam === 'function') {
    ({memberKey, memberId} = keyParam(options));
  }

  return {
    memberKey,
    memberId,
  };
}

function loadSingleItemFromStorage(
  storageKey,
  memberId,
  storageForMember,
  memberConfig,
  options = {},
) {
  let result;
  const record = storageForMember.getItem(storageKey);

  if (record) {
    if (memberConfig.expiresIn && !options.ignoreExpiration) {
      const isValid = !isRecordExpired(record, memberId, memberConfig);

      if (isValid) {
        result = record.value;
      }
    } else if (memberConfig.validator) {
      const isValid = memberConfig.validator(record, memberId, memberConfig);

      if (isValid) {
        result = record.value;
      } else {
        result = memberConfig.defaultValue;
      }
    } else {
      result = record.value;
    }
  } else {
    result = memberConfig.defaultValue;
  }

  if (memberConfig.onLoad) {
    result = memberConfig.onLoad(result, record);
  }

  return result;
}

class Store {
  constructor(storePrefix, memberConfigs = {}) {
    this.storePrefix = storePrefix;
    this.memberConfigs = memberConfigs;
    this.storages = {};
    this.storages[STORAGE_TYPES.SESSION] = persistenceManager.getSessionStorage();
    this.storages[STORAGE_TYPES.LOCAL] = persistenceManager.getLocalStorage();
    persistenceManager.registerStore(this);
  }

  createMemberInfo(keyParam, options) {
    const {memberKey, memberId} = createMemberIdAndKey(keyParam, options);
    const memberConfig = this.memberConfigs[memberId];
    const {storageType} = memberConfig;
    const storageForMember = this.storages[storageType];
    const storageKey = `${this.storePrefix}.${memberKey}`;

    return {
      memberId,
      storageKey,
      memberConfig,
      storageForMember,
    };
  }

  loadData(key, {options} = {}) {
    const {storageKey, storageForMember} = this.createMemberInfo(key, options);

    return storageForMember.getItem(storageKey);
  }

  find(key, {options} = {}) {
    const {storageKey, memberId, memberConfig} = this.createMemberInfo(key, options);
    let foundStorageType, loadResult;

    for (let index = 0; index < memberConfig.storageTypes.length; index++) {
      const storageType = memberConfig.storageTypes[index];

      loadResult = loadSingleItemFromStorage(storageKey, memberId, this.storages[storageType], memberConfig);

      if (loadResult) {
        foundStorageType = storageType;
        break;
      }
    }

    return {
      result: loadResult,
      storageType: foundStorageType,
    };
  }

  load(key, {options} = {}) {
    const {storageKey, memberId, memberConfig, storageForMember} = this.createMemberInfo(key, options);

    return loadSingleItemFromStorage(storageKey, memberId, storageForMember, memberConfig);
  }

  loadWithoutExpiration(key, {options} = {}) {
    const {storageKey, memberId, memberConfig, storageForMember} = this.createMemberInfo(key, options);

    return loadSingleItemFromStorage(
      storageKey,
      memberId,
      storageForMember,
      memberConfig,
      {
        ignoreExpiration: true,
      },
    );
  }

  loadMetaData(key, {options} = {}) {
    let result;
    const {storageKey, storageForMember} = this.createMemberInfo(key, options);
    const record = storageForMember.getItem(storageKey);

    if (record) {
      result = record.meta;
    } else {
      result = null;
    }

    return result;
  }

  save(key, value, {meta, options} = {}) {
    const {storageKey, memberConfig, storageForMember} = this.createMemberInfo(key, options);
    const record = {
      value,
      meta: {
        ...meta,
        createdAtUnix: Date.now(),
      },
    };

    if (memberConfig.onSave) {
      record.value = memberConfig.onSave(value);
    }

    storageForMember.setItem(storageKey, record);
  }

  update(key, value, {meta, options} = {}) {
    const {storageKey, storageForMember} = this.createMemberInfo(key, options);
    const record = this.loadData(key) || {};

    if (value) {
      record.value = value;
    }

    if (meta) {
      record.meta = meta;
    }

    storageForMember.setItem(storageKey, record);
  }

  updateMetaData(key, {meta, options} = {}) {
    const {storageKey, storageForMember} = this.createMemberInfo(key, options);
    let record = storageForMember.getItem(storageKey);

    if (!record) {
      record = {};
    }

    record.meta = {
      ...record.meta,
      ...meta,
    };
    storageForMember.setItem(storageKey, record);
  }

  remove(key, {options} = {}) {
    const {storageKey, storageForMember} = this.createMemberInfo(key, options);

    storageForMember.removeItem(storageKey);
  }

  clear() {
    Object.keys(this.memberConfigs).
      forEach((key) => {
        const {storageForMember, storageKey, memberConfig} = this.createMemberInfo(key);

        if (storageForMember) {
          storageForMember.removeItem(storageKey);
        } else {
          const {storageTypes} = memberConfig;

          storageTypes.forEach((possibleStorageType) => {
            const possibleStorageForMember = this.storages[possibleStorageType];

            possibleStorageForMember.removeItem(storageKey);
          });
        }
      });
  }

  setActiveMemberStorageTypeToLocal(keyParam) {
    this.setActiveMemberStorageType(keyParam, STORAGE_TYPES.LOCAL);
  }

  setActiveMemberStorageTypeToSession(keyParam) {
    this.setActiveMemberStorageType(keyParam, STORAGE_TYPES.SESSION);
  }

  setActiveMemberStorageType(keyParam, storageType, options) {
    const {memberId} = createMemberIdAndKey(keyParam, options);

    this.memberConfigs[memberId].storageType = storageType;
  }

  getActiveMemberStorageType(keyParam, options) {
    const {memberId} = createMemberIdAndKey(keyParam, options);

    return this.memberConfigs[memberId].storageType;
  }
}

export default Store;
